import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiRefreshCw,
  FiSearch,
} from "react-icons/fi";
import { apiURL } from "../config/config";
import { isAdmin } from "../utils/buttonVisibility";

const THEME = {
  accentText: "text-indigo-600 dark:text-indigo-300",
  accentBox:
    "border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-900/60 dark:bg-indigo-950/30 dark:text-indigo-200",
  focus: "focus:border-indigo-500 focus:ring-indigo-200 dark:focus:ring-indigo-900",
  button: "bg-indigo-600 text-white hover:bg-indigo-700",
  confirmColor: "#4f46e5",
};

const getAuthConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

const getQuotationFlow = (quotation) => {
  if (!quotation) return [];

  const approvalHierarchy = quotation.approvalHierarchy;
  if (Array.isArray(approvalHierarchy?.[0]?.approvalHierarchy)) {
    return approvalHierarchy[0].approvalHierarchy;
  }

  return Array.isArray(approvalHierarchy) ? approvalHierarchy : [];
};

const getDocumentNumber = (quotation = {}) => {
  const year = quotation.documentDate
    ? new Date(quotation.documentDate).getFullYear()
    : new Date(quotation.createdAt || Date.now()).getFullYear();
  const runNumber = quotation.runNumber ? String(quotation.runNumber) : "-";
  const prefix = quotation.createdByUser?.includes("@optx") ? "OPTX-QT" : "QT";

  return (
    quotation.documentNo ||
    `${prefix}(${quotation.type || "M"})-${year}-${runNumber}`
  );
};

const RepairApprovalFlow = () => {
  const navigate = useNavigate();
  const [quotationId, setQuotationId] = useState("");
  const [email, setEmail] = useState("");
  const [quotation, setQuotation] = useState(null);
  const [loadingQt, setLoadingQt] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [lastRepairAt, setLastRepairAt] = useState("");

  const flowSteps = getQuotationFlow(quotation);

  const fetchQuotation = async (id = quotationId.trim()) => {
    if (!id) {
      toast.error("กรุณาระบุ Quotation ID");
      return null;
    }

    setLoadingQt(true);
    try {
      const response = await axios.get(
        `${apiURL}quotations/${id}`,
        getAuthConfig()
      );
      setQuotation(response.data);
      return response.data;
    } catch (error) {
      setQuotation(null);
      toast.error(error?.response?.data?.message || "ไม่สามารถโหลด QT ได้");
      return null;
    } finally {
      setLoadingQt(false);
    }
  };

  const handleRepair = async () => {
    const id = quotationId.trim();
    const flowOwnerEmail = email.trim().toLowerCase();

    if (!id) {
      toast.error("กรุณาระบุ Quotation ID");
      return;
    }

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Re-assign approval flow?",
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.6">
          <div><strong>Quotation ID:</strong> ${id}</div>
          <div><strong>Flow source:</strong> ${
            flowOwnerEmail || "Original document creator"
          }</div>
          <div style="margin-top:8px;color:#6b7280">
            Approval progress will be reset and rebuilt from the latest user flow.
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Re-assign Flow",
      cancelButtonText: "Cancel",
      confirmButtonColor: THEME.confirmColor,
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!confirm.isConfirmed) return;

    setRepairing(true);
    try {
      Swal.fire({
        title: "Re-assigning flow...",
        html: "Please wait while the approval flow is updated and the QT is reloaded.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const payload = flowOwnerEmail ? { email: flowOwnerEmail } : {};
      await axios.patch(
        `${apiURL}quotations/${id}/update-approval-flow`,
        payload,
        getAuthConfig()
      );

      const refreshed = await fetchQuotation(id);
      if (refreshed) {
        setLastRepairAt(new Date().toLocaleString("th-TH"));
      }

      await Swal.fire({
        icon: "success",
        title: "Approval flow repaired",
        text: "The QT has been reloaded with the latest approval flow.",
        confirmButtonColor: THEME.confirmColor,
      });
      toast.success("ซ่อม approval flow สำเร็จ");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to re-assign flow",
        text: error?.response?.data?.message || "Please try again.",
        confirmButtonColor: "#dc2626",
      });
      toast.error(
        error?.response?.data?.message || "ซ่อม approval flow ไม่สำเร็จ"
      );
    } finally {
      setRepairing(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
        <div className="mx-auto max-w-3xl rounded-xl border border-red-200 bg-red-50 p-6 dark:border-red-900/60 dark:bg-red-950/30">
          <FiAlertTriangle className="mb-3 text-3xl text-red-500" />
          <h1 className="text-xl font-semibold">Admin only</h1>
          <p className="mt-2 text-sm text-red-700 dark:text-red-200">
            หน้านี้ใช้สำหรับ admin เพื่อซ่อม approval flow ของ QT เท่านั้น
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-5 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 text-gray-900 dark:bg-gray-950 dark:text-gray-100 md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <p className={`text-sm font-semibold uppercase tracking-wide ${THEME.accentText}`}>
                Admin Tool
              </p>
              <h1 className="mt-1 text-2xl font-bold">
                Repair QT Approval Flow
              </h1>
              <p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
                ใช้สำหรับ re-assign approval flow ให้ QT ที่อนุมัติค้าง เพราะผู้อนุมัติเดิมถูกลบออกจาก flow หรือมีการเปลี่ยน flow ของผู้ใช้
              </p>
            </div>
            <div className={`rounded-lg border px-4 py-3 text-sm ${THEME.accentBox}`}>
              ถ้าไม่ใส่ email ระบบจะใช้ flow ล่าสุดของผู้สร้างเอกสารเดิม
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h2 className="text-lg font-semibold">ค้นหาและซ่อม Flow</h2>
            <div className="mt-5 space-y-4">
              <TextInput
                label="Quotation ID"
                value={quotationId}
                onChange={setQuotationId}
                placeholder="เช่น 68f1c7a64e9e189ad9f0d55a"
              />
              <TextInput
                label="Flow owner email (optional)"
                value={email}
                onChange={setEmail}
                placeholder="example@optx.co.th"
                help="ใส่เฉพาะเมื่อต้องการบังคับใช้ flow ของ user คนอื่น"
              />

              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => fetchQuotation()}
                  disabled={loadingQt || repairing}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                  <FiSearch />
                  {loadingQt ? "Loading..." : "Load QT"}
                </button>
                <button
                  type="button"
                  onClick={handleRepair}
                  disabled={loadingQt || repairing}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${THEME.button}`}
                >
                  <FiRefreshCw className={repairing ? "animate-spin" : ""} />
                  {repairing ? "Repairing..." : "Re-assign Flow"}
                </button>
              </div>

              {lastRepairAt && (
                <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-200">
                  <FiCheckCircle className="mt-0.5 shrink-0" />
                  <span>ซ่อมล่าสุดเมื่อ {lastRepairAt} และโหลดข้อมูล QT ใหม่แล้ว</span>
                </div>
              )}
            </div>
          </section>

          <QuotationPreview quotation={quotation} flowSteps={flowSteps} />
        </div>
      </div>
    </div>
  );
};

const TextInput = ({ label, value, onChange, placeholder, help }) => (
  <div>
    <label className="mb-1 block text-sm font-medium">{label}</label>
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className={`w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 dark:border-gray-700 dark:bg-gray-950 ${THEME.focus}`}
    />
    {help && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{help}</p>}
  </div>
);

const QuotationPreview = ({ quotation, flowSteps }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-lg font-semibold">QT Preview</h2>
      {quotation && (
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
          {quotation.approvalStatus || quotation.status || "Unknown"}
        </span>
      )}
    </div>

    {!quotation ? (
      <div className="mt-5 rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
        ระบุ Quotation ID แล้วกด Load QT เพื่อดูข้อมูลก่อนซ่อม
      </div>
    ) : (
      <div className="mt-5 space-y-5">
        <div className="grid gap-3 md:grid-cols-2">
          <Info label="Document No." value={getDocumentNumber(quotation)} />
          <Info label="Created By" value={quotation.createdByUser || "-"} />
          <Info
            label="Project"
            value={quotation.projectName || quotation.title || "-"}
          />
          <Info
            label="Client"
            value={quotation.clientId?.customerName || quotation.client || "-"}
          />
        </div>

        <div>
          <h3 className="mb-3 text-sm font-semibold">Current Approval Flow</h3>
          {flowSteps.length === 0 ? (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800 dark:border-yellow-900/60 dark:bg-yellow-950/30 dark:text-yellow-200">
              ไม่พบ approval flow ใน QT นี้
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-800">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-950 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3">Level</th>
                    <th className="px-4 py-3">Approver</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {flowSteps.map((step, index) => (
                    <tr key={`${step.level}-${step.approver}-${index}`}>
                      <td className="px-4 py-3 font-semibold">
                        Lv.{step.level || "-"}
                      </td>
                      <td className="px-4 py-3">
                        {step.approver || step.email || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                          {step.status || "Pending"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    )}
  </section>
);

const Info = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
    <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
      {label}
    </div>
    <div className="mt-1 break-words text-sm font-medium">{value}</div>
  </div>
);

export default RepairApprovalFlow;
